import { useQuery } from "@tanstack/react-query";

import { useAuth } from "@/providers";
import { useDomainServices } from "@/providers/DomainServices";

import { viewQueryKeys } from "./viewQueryKeys";

/**
 * One View by id — what a shared link resolves. Not keyed by target, since an id
 * identifies a View outright, but keyed by org: the same id reads as absent from
 * any org but its own. Disabled until an id is supplied.
 */
export function useView(
  viewId: string | undefined,
  { staleTime }: { staleTime?: number } = {},
) {
  // Resolved inside the query function, not here: the context throws on a
  // service it was not given, and a disabled query has no business asking.
  const services = useDomainServices();
  const { currentOrganization } = useAuth();
  const orgId = currentOrganization?.org_id;

  return useQuery({
    queryKey: viewQueryKeys.id(orgId, viewId ?? ""),
    enabled: viewId !== undefined,
    staleTime,
    queryFn: async ({ signal }) => {
      if (orgId === undefined) {
        return Promise.reject(new Error("CurrentOrganization is not set"));
      }
      if (viewId === undefined) {
        return Promise.reject(new Error("No viewId supplied"));
      }

      return services.viewService.getViewById(viewId, orgId, {
        abortSignal: signal,
      });
    },
  });
}
